import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import * as Sentry from "@sentry/react";
import { nanoid } from "nanoid";
import create from "zustand";
import { persist } from "zustand/middleware";
import { base64ToBlob, blobToBase64 } from "./blobUtils";
import { ensureDirectoryExists } from "./fsUtils";
import {
  deleteFromIndexedDB,
  getFromIndexedDB,
  saveToIndexedDB,
} from "./indexedDBStorage";

const OFFLINE_UPLOAD_CONFIG = {
  AUTO_PURGE_HOURS: 24,
  MAX_FILE_SIZE: 50 * 1024 * 1024,
  MAX_PENDING_UPLOADS: 50,
  MAX_TOTAL_STORAGE: 500 * 1024 * 1024,
  PURGE_COMPLETED_HOURS: 2,
  PURGE_ERROR_HOURS: 1,
} as const;

export interface OfflineUpload {
  id: string;
  context: "chat" | "task";
  ownerId: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  path: string;
  addedAt: string;
  status: "complete" | "error" | "pending" | "uploading";
  progress: number;
  assetUrl?: string;
  errorMessage?: string;
  retryCount: number;
}

interface OfflineUploadsState {
  uploads: OfflineUpload[];
  isProcessing: boolean;

  addUpload: (
    context: "chat" | "task",
    ownerId: string,
    file: File
  ) => Promise<
    | { error: string; errorCode: string; success: false }
    | { success: true; uploadId: string }
  >;
  getStoredFile: (id: string) => Promise<File | null>;
  removeUpload: (id: string) => Promise<void>;
  markComplete: (id: string, assetUrl: string) => void;
  markError: (id: string, errorMessage: string) => void;
  updateProgress: (id: string, progress: number) => void;

  getUploadsForMessage: (messageId: string) => OfflineUpload[];
  getUploadsForTask: (taskId: string) => OfflineUpload[];
  getById: (id: string) => OfflineUpload | undefined;

  removeUploadsForMessage: (messageId: string) => Promise<void>;
  removeUploadsForTask: (taskId: string) => Promise<void>;
  purgeOld: (hours: number) => Promise<void>;
  purgeCompleted: () => Promise<void>;
  purgeErrors: () => Promise<void>;
  enforceStorageLimits: () => Promise<void>;
  getStorageStats: () => {
    totalSize: number;
    totalFiles: number;
    pendingFiles: number;
  };

  setProcessing: (isProcessing: boolean) => void;
}

const isNative = Capacitor.isNativePlatform();

export const useOfflineUploadsStore = create<OfflineUploadsState>(
  persist(
    (set, get) => ({
      addUpload: async (
        context: "chat" | "task",
        ownerId: string,
        file: File
      ) => {
        // Validate file size
        if (file.size > OFFLINE_UPLOAD_CONFIG.MAX_FILE_SIZE) {
          const error = new Error(
            `File size ${file.size} bytes exceeds maximum allowed size of ${OFFLINE_UPLOAD_CONFIG.MAX_FILE_SIZE} bytes`
          );
          Sentry.captureException(error, {
            extra: {
              fileName: file.name,
              fileSize: file.size,
              maxSize: OFFLINE_UPLOAD_CONFIG.MAX_FILE_SIZE,
            },
            tags: {
              action: "file-size-validation",
              component: "offline-uploads-store",
            },
          });
          return {
            error: "File too large",
            errorCode: "FILE_TOO_LARGE",
            success: false,
          };
        }

        const state = get();
        const { totalSize } = state.getStorageStats();

        const existingUploadsForContext = state.uploads.filter(
          (u) => u.context === context && u.ownerId === ownerId
        );

        if (existingUploadsForContext.length >= 10) {
          const error = new Error(
            `Cannot add more files to this ${context}. Maximum of 10 files per ${context} reached. Please delete some files before adding new ones.`
          );
          Sentry.captureException(error, {
            extra: {
              context,
              currentFiles: existingUploadsForContext.length,
              fileName: file.name,
              maxFilesPerContext: 10,
              ownerId,
            },
            tags: {
              action: "context-files-limit-reached",
              component: "offline-uploads-store",
            },
          });
          return {
            error: "Too many files",
            errorCode: "TOO_MANY_FILES",
            success: false,
          };
        }

        if (totalSize + file.size > OFFLINE_UPLOAD_CONFIG.MAX_TOTAL_STORAGE) {
          const error = new Error(
            `Cannot add file. Adding this file would exceed storage limit. Current: ${totalSize} bytes, Max: ${OFFLINE_UPLOAD_CONFIG.MAX_TOTAL_STORAGE} bytes. Please delete some files before adding new ones.`
          );
          Sentry.captureException(error, {
            extra: {
              currentSize: totalSize,
              fileName: file.name,
              fileSize: file.size,
              maxSize: OFFLINE_UPLOAD_CONFIG.MAX_TOTAL_STORAGE,
            },
            tags: {
              action: "storage-limit-would-exceed",
              component: "offline-uploads-store",
            },
          });
          return {
            error: "Storage limit exceeded",
            errorCode: "STORAGE_LIMIT_EXCEEDED",
            success: false,
          };
        }

        const id = nanoid();
        const normalizedFileName = file.name
          .replace(/[^a-zA-Z0-9.-]/g, "_")
          .replace(/_{2,}/g, "_");

        const fileExtension = normalizedFileName.split(".").pop() ?? "";
        const safeFileName = `${id}.${fileExtension}`;
        const path = `offline-uploads/${context}/${ownerId}/${safeFileName}`;

        try {
          if (isNative) {
            await ensureDirectoryExists(path);
            const base64Data = await blobToBase64(file);

            await Filesystem.writeFile({
              data: base64Data,
              directory: Directory.Data,
              path,
            });
          } else {
            await saveToIndexedDB(path, file);
          }

          const upload: OfflineUpload = {
            addedAt: new Date().toISOString(),
            context,
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
            id,
            ownerId,
            path,
            progress: 0,
            retryCount: 0,
            status: "pending",
          };

          set((state) => ({ uploads: [...state.uploads, upload] }));
          return { success: true, uploadId: id };
        } catch (error) {
          Sentry.captureException(error, {
            extra: {
              context,
              fileName: file.name,
              id,
              isNative,
              ownerId,
              path,
            },
            tags: { action: "add-upload", component: "offline-uploads-store" },
          });
          return {
            error: "Failed to add file",
            errorCode: "ADD_FILE_FAILED",
            success: false,
          };
        }
      },

      enforceStorageLimits: async () => {
        const state = get();
        const { pendingFiles, totalFiles, totalSize } = state.getStorageStats();

        if (totalSize > OFFLINE_UPLOAD_CONFIG.MAX_TOTAL_STORAGE) {
          const error = new Error(
            `Total storage limit exceeded. Current: ${totalSize} bytes, Max: ${OFFLINE_UPLOAD_CONFIG.MAX_TOTAL_STORAGE} bytes`
          );
          Sentry.captureException(error, {
            extra: {
              currentSize: totalSize,
              maxSize: OFFLINE_UPLOAD_CONFIG.MAX_TOTAL_STORAGE,
            },
            tags: {
              action: "storage-limit-exceeded",
              component: "offline-uploads-store",
            },
          });
          throw error;
        }

        if (pendingFiles > OFFLINE_UPLOAD_CONFIG.MAX_PENDING_UPLOADS) {
          const error = new Error(
            `Pending uploads limit exceeded. Current: ${pendingFiles}, Max: ${OFFLINE_UPLOAD_CONFIG.MAX_PENDING_UPLOADS}`
          );
          Sentry.captureException(error, {
            extra: {
              currentPending: pendingFiles,
              maxPending: OFFLINE_UPLOAD_CONFIG.MAX_PENDING_UPLOADS,
            },
            tags: {
              action: "pending-uploads-limit-exceeded",
              component: "offline-uploads-store",
            },
          });
          throw error;
        }
      },

      getById: (id: string) => {
        return get().uploads.find((u) => u.id === id);
      },

      getStorageStats: () => {
        const uploads = get().uploads;
        const totalSize = uploads.reduce((sum, u) => sum + u.fileSize, 0);
        const totalFiles = uploads.length;
        const pendingFiles = uploads.filter(
          (u) => u.status === "pending" || u.status === "uploading"
        ).length;

        return { pendingFiles, totalFiles, totalSize };
      },

      getStoredFile: async (id: string) => {
        const upload = get().uploads.find((u) => u.id === id);
        if (!upload) {
          return null;
        }

        try {
          if (isNative) {
            await ensureDirectoryExists(upload.path);

            const { data } = await Filesystem.readFile({
              directory: Directory.Data,
              path: upload.path,
            });

            if (typeof data === "string") {
              const blob = await base64ToBlob(data, upload.fileType);
              const file = new File([blob], upload.fileName, {
                type: upload.fileType,
              });
              return file;
            }
          } else {
            const blob = await getFromIndexedDB(upload.path);
            if (blob) {
              const file = new File([blob], upload.fileName, {
                type: upload.fileType,
              });
              return file;
            }
          }
        } catch (error) {
          Sentry.captureException(error, {
            extra: {
              fileName: upload.fileName,
              id,
              isNative,
              path: upload.path,
            },
            tags: {
              action: "get-stored-file",
              component: "offline-uploads-store",
            },
          });
        }

        return null;
      },

      getUploadsForMessage: (messageId: string) => {
        const uploads = get().uploads.filter(
          (u) => u.context === "chat" && u.ownerId === messageId
        );
        return uploads;
      },

      getUploadsForTask: (taskId: string) => {
        const uploads = get().uploads.filter(
          (u) => u.context === "task" && u.ownerId === taskId
        );
        return uploads;
      },

      isProcessing: false,

      markComplete: (id: string, assetUrl: string) => {
        set((state) => ({
          uploads: state.uploads.map((u) =>
            u.id === id
              ? { ...u, assetUrl, progress: 100, status: "complete" }
              : u
          ),
        }));
      },

      markError: (id: string, errorMessage: string) => {
        set((state) => ({
          uploads: state.uploads.map((u) =>
            u.id === id ? { ...u, errorMessage, status: "error" } : u
          ),
        }));
      },

      purgeCompleted: async () => {
        const cutoffTime = new Date();
        cutoffTime.setHours(
          cutoffTime.getHours() - OFFLINE_UPLOAD_CONFIG.PURGE_COMPLETED_HOURS
        );

        const completedUploads = get().uploads.filter(
          (u) => u.status === "complete" && new Date(u.addedAt) < cutoffTime
        );

        for (const upload of completedUploads) {
          await get().removeUpload(upload.id);
        }
      },

      purgeErrors: async () => {
        const cutoffTime = new Date();
        cutoffTime.setHours(
          cutoffTime.getHours() - OFFLINE_UPLOAD_CONFIG.PURGE_ERROR_HOURS
        );

        const errorUploads = get().uploads.filter(
          (u) => u.status === "error" && new Date(u.addedAt) < cutoffTime
        );

        for (const upload of errorUploads) {
          await get().removeUpload(upload.id);
        }
      },

      purgeOld: async (hours: number) => {
        const cutoffTime = new Date();
        cutoffTime.setHours(cutoffTime.getHours() - hours);

        const oldUploads = get().uploads.filter(
          (u) => new Date(u.addedAt) < cutoffTime
        );

        for (const upload of oldUploads) {
          await get().removeUpload(upload.id);
        }
      },

      removeUpload: async (id: string) => {
        const upload = get().uploads.find((u) => u.id === id);
        if (!upload) {
          return;
        }

        try {
          if (isNative) {
            await Filesystem.deleteFile({
              directory: Directory.Data,
              path: upload.path,
            });
          } else {
            await deleteFromIndexedDB(upload.path);
          }

          set((state) => ({
            uploads: state.uploads.filter((u) => u.id !== id),
          }));
        } catch (error) {
          Sentry.captureException(error, {
            extra: {
              fileName: upload.fileName,
              id,
              isNative,
              path: upload.path,
            },
            tags: {
              action: "remove-upload",
              component: "offline-uploads-store",
            },
          });
        }
      },

      removeUploadsForMessage: async (messageId: string) => {
        const uploads = get().getUploadsForMessage(messageId);
        for (const upload of uploads) {
          await get().removeUpload(upload.id);
        }
      },

      removeUploadsForTask: async (taskId: string) => {
        const uploads = get().getUploadsForTask(taskId);
        for (const upload of uploads) {
          await get().removeUpload(upload.id);
        }
      },

      setProcessing: (isProcessing: boolean) => {
        set({ isProcessing });
      },

      updateProgress: (id: string, progress: number) => {
        set((state) => ({
          uploads: state.uploads.map((u) =>
            u.id === id ? { ...u, progress } : u
          ),
        }));
      },

      uploads: [],
    }),
    {
      name: "offline-uploads",
      version: 1,
    }
  )
);

// Export configuration for external use
export { OFFLINE_UPLOAD_CONFIG };

export function getUploadErrorMessage(
  errorCode: string,
  context?: string
): string {
  switch (errorCode) {
    case "FILE_TOO_LARGE":
      return "File is too large. Maximum file size is 50MB.";
    case "TOO_MANY_FILES":
      return `Cannot add more files to this ${
        context ?? "item"
      }. Maximum of 10 files reached. Please delete some files before adding new ones.`;
    case "STORAGE_LIMIT_EXCEEDED":
      return "Storage limit exceeded. Please delete some files before adding new ones.";
    case "ADD_FILE_FAILED":
      return "Failed to add file. Please try again.";
    default:
      return "An error occurred while adding the file.";
  }
}
